/**
 * Pure quote helpers.
 *
 * Kept out of `QuoteBits.tsx` so that file only exports components and fast
 * refresh keeps working — the same split as `invoices/invoice-utils.ts`.
 *
 * Everything here is date arithmetic on plain 'YYYY-MM-DD' strings, and it all
 * stays on this side of the wire on purpose. Expiry is `validTill` measured
 * against the VIEWER's today in the VIEWER's timezone, so there is deliberately
 * no `isExpired` on the record and there never will be: a boolean computed on
 * the server is already answering the wrong question for anyone east or west of
 * it, and one baked into a cached response goes stale the moment midnight
 * passes. The client is the only place that knows what "today" means here.
 */
import { normaliseStatusName, todayIsoDate } from '@/lib/invoice-utils';
import type { MasterRef, Quote, QuoteProduct, QuoteStage } from '@/lib/types';

/**
 * Just the field the expiry tests actually read.
 *
 * A whole `Quote` satisfies this, so every existing caller is unaffected — but
 * a badge that has only a date no longer has to fake a record to ask the
 * question. Narrow parameters are what stop a cast creeping in.
 */
export type QuoteExpiry = Pick<Quote, 'validTill'>;

/**
 * Expiry is a pure string comparison — ISO dates sort lexicographically.
 *
 * Never build a `Date` from `validTill`. `new Date('2026-08-24')` is parsed as
 * UTC midnight and then read back in local time, which moves the day backwards
 * for every viewer west of Greenwich; comparing the strings has no timezone in
 * it at all. `todayIsoDate` is the mirror of the same rule — it assembles today
 * from local calendar parts rather than `toISOString()`, which would hand back
 * tomorrow's date for anyone east of Greenwich after their local evening and so
 * expire a quote a day early.
 *
 * `null` is open-ended, which is not expired.
 */
export function isQuoteExpired(quote: QuoteExpiry): boolean {
  return quote.validTill !== null && quote.validTill < todayIsoDate();
}

/**
 * Inside the window where someone should chase the quote before it lapses.
 *
 * Already-expired quotes are excluded rather than folded in: the two states
 * want different words and different urgency, and a badge that says "expiring
 * soon" about something that lapsed last month is worse than no badge. An
 * open-ended quote (`validTill === null`) matches neither.
 */
export function isExpiringSoon(quote: QuoteExpiry, days = 14): boolean {
  if (quote.validTill === null) return false;
  const today = todayIsoDate();
  if (quote.validTill < today) return false;
  return quote.validTill <= addDaysIso(today, days);
}

/**
 * Add days to a 'YYYY-MM-DD' string and get one back.
 *
 * Built from local calendar parts on both ends. The `Date` here is only ever a
 * calendar calculator — constructed from explicit local components and read
 * back through the local getters, so it never touches UTC — and the month
 * overflow of `new Date(y, m, d + n)` is exactly the month-length and leap-year
 * arithmetic we want. `toISOString()` would undo all of that in one call.
 */
export function addDaysIso(iso: string, days: number): string {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  const day = Number(iso.slice(8, 10));
  const shifted = new Date(year, month - 1, day + days);
  const shiftedMonth = String(shifted.getMonth() + 1).padStart(2, '0');
  const shiftedDay = String(shifted.getDate()).padStart(2, '0');
  return `${shifted.getFullYear()}-${shiftedMonth}-${shiftedDay}`;
}

/**
 * The stage names meaning the customer said YES, and the ones meaning they said
 * NO.
 *
 * ⚠️ **BOTH ARE NOW THE FALLBACK, NOT THE DECISION.** Migration 080 added
 * `is_accepted` and `is_declined` to `f_quote_stages`; `isStageAccepted()` and
 * `isStageDeclined()` read those and reach these sets only when the flag is
 * absent — a pre-080 server, a rollback, or a caller with no master list.
 *
 * These are the five rows that exist today: Draft, Sent, Accepted, Declined,
 * Expired, of which only Draft and Accepted came from Zoho. Nothing else is
 * guessed at, deliberately — a speculative entry would make these look more
 * authoritative than they are.
 *
 * ⚠️ Matched EXACTLY, after case-folding and whitespace collapse, and NEVER by
 * containment. `includes('accept')` is what this replaced, and it painted
 * **"Not Accepted"** and **"Pending Acceptance"** as agreement — the two most
 * obvious stages anyone would ever add, both saying the customer had agreed
 * when they had not. **Do not reintroduce it.** The flag removes the need for
 * the guess; it does not make the guess safe.
 *
 * ⚠️ **`Expired` is in NEITHER set, matching the server's own flags.** A
 * declined quote is one the customer answered; an expired one is a quote nobody
 * answered at all. Never derive "declined" as `!accepted`.
 */
const ACCEPTED_STAGE_NAMES: ReadonlySet<string> = new Set(['accepted']);
const DECLINED_STAGE_NAMES: ReadonlySet<string> = new Set(['declined']);

/**
 * Resolve one stage flag — flag first, exact name only as a fallback.
 *
 * ⚠️ **THE FLAGS ARE NOT ON THE DOCUMENT.** A quote's own `stage` is a bare
 * `MasterRef` (`{id, name}`) from the server's `toLookup()`, so it carries
 * neither flag. They live on the `GET /quote-stages` row, which is why the
 * master list is a parameter and the resolution is by id.
 *
 * The record is checked for its own flag first regardless, so that the day
 * these are embedded on the document this reads them with no change here.
 * `typeof` rather than truthiness: `false` is a real answer from the server and
 * must not fall through to a name set that could contradict it.
 *
 * ⚠️ **Every unknown resolves to `false`**, and for the quote surface that is
 * the same asymmetry the invoice surface has: "not accepted" is the visible
 * wrong answer and "accepted" is the silent one. A quote wrongly shown as
 * unaccepted is a question somebody asks; a quote wrongly shown as accepted
 * says a customer agreed to a figure they never saw — and acceptance is the
 * thing that raises an invoice and pushes it to Xero.
 */
function stageFlag(
  stage: MasterRef | null,
  stages: readonly QuoteStage[] | undefined,
  key: 'isAccepted' | 'isDeclined',
  fallbackNames: ReadonlySet<string>,
): boolean {
  if (!stage) return false;

  const embedded = (stage as Partial<QuoteStage>)[key];
  if (typeof embedded === 'boolean') return embedded;

  const row = stages?.find((candidate) => candidate.id === stage.id);
  const flag = row?.[key];
  if (typeof flag === 'boolean') return flag;

  return fallbackNames.has(normaliseStatusName(stage.name));
}

/** Whether the customer has agreed to this quote. See {@link stageFlag}. */
export function isStageAccepted(
  stage: MasterRef | null,
  stages?: readonly QuoteStage[],
): boolean {
  return stageFlag(stage, stages, 'isAccepted', ACCEPTED_STAGE_NAMES);
}

/**
 * Whether the customer has actively refused this quote. See {@link stageFlag}.
 *
 * ⚠️ Not the complement of {@link isStageAccepted}. `Expired`, `Draft` and
 * `Sent` are all neither.
 */
export function isStageDeclined(
  stage: MasterRef | null,
  stages?: readonly QuoteStage[],
): boolean {
  return stageFlag(stage, stages, 'isDeclined', DECLINED_STAGE_NAMES);
}

/**
 * True when a quote's totals were typed by a human and adding a line would
 * destroy them.
 *
 * The quotes analogue of `isLegacyImported`, but the hazard arrived from the
 * other direction: unlike the invoices, the three Zoho quote imports carry no
 * money at all, so what this catches is a quote whose header figures someone
 * entered by hand instead of posting lines. Either way the consequence is the
 * same — the first line-item create, update or delete makes the server rewrite
 * every header total from the lines, in the same transaction, with no
 * confirmation and no record of what was there before.
 *
 * Only `subTotal` and `grandTotal` are tested. `discountTotal` and `taxTotal`
 * are nullable and are null on every imported quote, so a `!== '0.00'` check on
 * them fires on records that have no hand-entered money whatsoever.
 */
export function hasHandEnteredTotals(quote: Quote, lines: QuoteProduct[]): boolean {
  return lines.length === 0 && (quote.grandTotal !== '0.00' || quote.subTotal !== '0.00');
}

/*
 * THE CONVERT REFUSAL THAT IS NOT A FAILURE.
 *
 * `POST /quotes/{id}/convert` bills a whole quote in ONE invoice. A quote whose
 * `billingMode` is `'staged'` is billed a LINE ITEM AT A TIME, through a
 * different endpoint entirely (`POST /quotes/{id}/invoices`, which takes
 * `quoteProductIds`), so convert is refused outright: `409 QUOTE_IS_STAGED`.
 *
 * Nothing is wrong with the quote. Staged is a chosen configuration, not a
 * fault, and the copy below must never read as though the record were broken or
 * as though something went wrong — the API is pointing at the other door.
 *
 * ⚠️ **Matched on `error.code`, NEVER on the message.** Convert answers 409 for
 * two unrelated reasons and they mean opposite things:
 *
 *   - `QUOTE_IS_STAGED`         — this quote is billed a different way, for ever
 *   - `QUOTE_ALREADY_CONVERTED` — an invoice already exists for this quote
 *
 * A message match cannot tell those apart, and this repo has twice shipped prose
 * matchers that the backend then reworded out from under — the line-item
 * refusals were reworded twice in two days. `error.code` is the discriminator.
 *
 * ⚠️ And an UNRECOGNISED code must fall through to the server's own message,
 * never to a bare "something went wrong". An unknown code means "newer than this
 * branch", never "not a refusal" — the same rule `xero.ts` states for its
 * refusal map.
 *
 * ⚠️ **MOVED HERE from `QuoteDetailPage`, on that constant's own instruction**
 * ("a better long-term home is `src/lib/quote-utils.ts` … move it whenever
 * either page is next opened"). It had to move: the Convert control on both
 * pages now opens `InvoiceFormModal`, so the modal is a THIRD reader, and a
 * component importing a page is an import cycle rather than a style problem.
 */
export const QUOTE_IS_STAGED_CODE = 'QUOTE_IS_STAGED';

/*
 * THE SECOND 409 — TRUE, CODED, AND STILL DESCRIBING SOMETHING NOBODY DID.
 *
 * Convert's other refusal now carries a code of its own (backend `8b3539b`).
 * ⚠️ **The MESSAGE did not change at all** — byte for byte what it always was —
 * so the old fall-through kept working right through the migration and nothing
 * broke while this branch was absent. That is the whole reason it is safe to
 * adopt: the code is new information, not a repair.
 *
 * ⚠️ **What the server COUNTS is not what its sentence SAYS.** The guard counts
 * every invoice carrying this `quote_id`, whichever endpoint raised it, and then
 * reports "has already been converted to invoice 13 (INV-0952)". A quote billed
 * in parts through `POST /quotes/{id}/invoices` therefore refuses convert for
 * ever, naming a conversion that never ran — while most of the quote sits
 * unbilled and the reader is being told the job is done. Same shape as every
 * entry in CLAUDE.md's proxy-field table: a field written for one purpose,
 * consulted as the answer to a different question, agreeing often enough that
 * the gap only opens in a state nobody sets up on purpose.
 *
 * The BEHAVIOUR is right — a whole quote must not be billed twice — so this is a
 * copy problem, and `InvoiceFormModal` answers it the only way a client can:
 * `GET /invoices?quoteId=` and name what actually exists, because the count of
 * documents is precisely the fact the message cannot express. ⚠️ **A failed
 * lookup falls back to the server's own sentence** — a lookup that did not
 * happen is not evidence about what exists, and inventing certainty here is
 * worse than repeating an imprecise sentence.
 */
export const QUOTE_ALREADY_CONVERTED_CODE = 'QUOTE_ALREADY_CONVERTED';

/**
 * `POST /quotes/{id}/invoices` refused because some of the chosen lines are
 * already on an invoice. The message and `details` are byte-identical to the
 * bare-code fallback this app already handled, so the fall-through kept
 * working throughout — the same shape as {@link QUOTE_ALREADY_CONVERTED_CODE}.
 *
 * ⚠️ **It exists because this endpoint raises TWO 422s on the SAME
 * `quoteProductIds` path that send a person to opposite places:**
 *
 * | refusal | routes to |
 * | --- | --- |
 * | `Line item(s) N are not on quote M` (bare code) | the chips in front of you — the selection is wrong |
 * | this one | **an invoice** — the selection was reasonable and the world moved |
 *
 * Neither `path` nor status can separate them, so a client keying by path gets
 * one slot for two opposite instructions. Same test that split
 * `PROJECT_HAS_NO_CITIES` from `CITIES_NOT_ON_PROJECT`: a refusal earns a code
 * when it routes somewhere the others do not.
 *
 * The "selection is wrong" case deliberately keeps the bare code — it is what
 * every other 422 here already means, and naming everything is ceremony nobody
 * reads.
 */
export const QUOTE_LINES_ALREADY_INVOICED_CODE = 'QUOTE_LINES_ALREADY_INVOICED';

/*
 * ⚠️ **Copy is the one part of this contract nothing typechecks.** A sentence
 * naming the ABSENCE of a control rots silently the moment that control ships,
 * because the person who builds it is not reading the refusal that denied it.
 * Grep for the SURFACE (the control's own name) when checking this is still
 * accurate, not for the wording.
 *
 * It names the control rather than describing it, and names the PAGE because
 * the disabled Convert button also appears on the list, where the button is not
 * on screen. A reader on either surface can follow it.
 *
 * ONE sentence, read by the disabled button's `title` on both list and detail
 * and by the 409 banner inside the convert modal, so the renderings cannot
 * drift. ⚠️ There must never be a second copy: a duplicate gets reworded in one
 * place and not the other, and nothing fails.
 */
export const STAGED_CONVERT_REASON =
  'This quote is billed one line item at a time, so converting doesn’t apply — open the quote and use “Bill line items” instead.';
